import React, { useEffect, useRef, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { SiteProvider } from "./contexts/SiteContext";
import Seo from "./components/Seo";
import CookieConsent from "./components/CookieConsent";
import FeedbackWidget from "./components/FeedbackWidget";
import { gaPageview } from "./lib/consent";
import { captureAttribution } from "./lib/attribution";
import { CurrencyProvider } from "./lib/currency";
import CmsPage from "./components/CmsPage";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./pages/AuthCallback";
import "./App.css";
// Route-based code-splitting: each page ships as its own chunk so the initial
// load (esp. on mobile) no longer pulls in admin/CMS/page-builder/etc. The
// Suspense boundary lives in Layout, around the router Outlet.
const Home = lazy(() => import("./pages/Home"));
const ProgramDetail = lazy(() => import("./pages/ProgramDetail"));
const Startups = lazy(() => import("./pages/Startups"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Events = lazy(() => import("./pages/Events"));
const EventPublish = lazy(() => import("./pages/EventPublish"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventCheckin = lazy(() => import("./pages/EventCheckin"));
const EventManage = lazy(() => import("./pages/EventManage"));
const Opportunities = lazy(() => import("./pages/Opportunities"));
const Ecosystem = lazy(() => import("./pages/Ecosystem"));
const Knowledge = lazy(() => import("./pages/Knowledge"));
const News = lazy(() => import("./pages/News"));
const Services = lazy(() => import("./pages/Services"));
const Jobs = lazy(() => import("./pages/Jobs"));
const PostJob = lazy(() => import("./pages/PostJob"));
const Mentors = lazy(() => import("./pages/Mentors"));
const OrgListing = lazy(() => import("./pages/OrgListing"));
const OrgContent = lazy(() => import("./pages/OrgContent"));
const Investors = lazy(() => import("./pages/Investors"));
const FoundersPage = lazy(() => import("./pages/FoundersPage"));
const SME = lazy(() => import("./pages/SME"));
const Community = lazy(() => import("./pages/Community"));
const About = lazy(() => import("./pages/About"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Profile = lazy(() => import("./pages/Profile"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DealFlow = lazy(() => import("./pages/DealFlow"));
const StartupDetail = lazy(() => import("./pages/StartupDetail"));
const CreateStartup = lazy(() => import("./pages/CreateStartup"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Cms = lazy(() => import("./pages/admin/Cms"));
const Femtech = lazy(() => import("./pages/Femtech"));
const FemtechAdmin = lazy(() => import("./pages/admin/FemtechAdmin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Tools = lazy(() => import("./pages/Tools"));
const BusinessPlanApp = lazy(() => import("./pages/BusinessPlanApp"));
const BusinessPlanWorkspace = lazy(() => import("./pages/BusinessPlanApp").then((m) => ({ default: m.BusinessPlanWorkspace })));
const BusinessPlanPrint = lazy(() => import("./pages/BusinessPlan").then((m) => ({ default: m.BusinessPlanPrint })));
const LegalNotice = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.LegalNotice })));
const DataProtection = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.DataProtection })));
const RegistrationInfo = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.RegistrationInfo })));
const AccessibilityStatement = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.AccessibilityStatement })));

// Reset scroll to the top on every route change (React Router keeps the previous
// scroll position otherwise — e.g. clicking Follow/Login from low on a profile
// would land the new page scrolled down at the footer).
function ScrollToTop() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    // GA4's config call already counts the landing page; only send on later route changes.
    if (first.current) { first.current = false; return; }
    gaPageview(pathname);
  }, [pathname]);
  return null;
}

function RootRouter() {
  const location = useLocation();
  // OAuth callback comes back as #session_id=... — process it before regular routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/opportunites" element={<Opportunities />} />
        <Route path="/programs" element={<Navigate to="/opportunites" replace />} />
        <Route path="/programs/:slug" element={<ProgramDetail />} />
        <Route path="/startups" element={<Startups />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/publish" element={<ProtectedRoute><EventPublish /></ProtectedRoute>} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/events/:id/checkin" element={<EventCheckin />} />
        <Route path="/events/:id/manage" element={<ProtectedRoute><EventManage /></ProtectedRoute>} />
        <Route path="/funding" element={<Navigate to="/opportunites" replace />} />
        <Route path="/outils" element={<Tools />} />
        <Route path="/legal" element={<LegalNotice />} />
        <Route path="/data-protection" element={<DataProtection />} />
        <Route path="/registration-info" element={<RegistrationInfo />} />
        <Route path="/accessibility" element={<AccessibilityStatement />} />
        <Route path="/ecosystem" element={<Ecosystem />} />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/femtech" element={<Femtech />} />
        <Route path="/actualites" element={<News />} />
        <Route path="/news" element={<Navigate to="/actualites" replace />} />
        <Route path="/services" element={<Services />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/mentors" element={<Mentors />} />
        <Route path="/organisations/new" element={<ProtectedRoute roles={["organisation", "admin"]}><OrgListing /></ProtectedRoute>} />
        <Route path="/organisations/:id/edit" element={<ProtectedRoute roles={["organisation", "admin"]}><OrgListing /></ProtectedRoute>} />
        <Route path="/organisations/content" element={<ProtectedRoute roles={["organisation", "admin"]} capability="can_publish_org_content"><OrgContent /></ProtectedRoute>} />
        <Route path="/jobs/new" element={<ProtectedRoute roles={["founder", "organisation", "admin"]}><PostJob /></ProtectedRoute>} />
        <Route path="/jobs/:id/edit" element={<ProtectedRoute roles={["founder", "organisation", "admin"]}><PostJob /></ProtectedRoute>} />
        <Route path="/investors" element={<CmsPage page="investors" append><Investors /></CmsPage>} />
        <Route path="/founders" element={<CmsPage page="founders" append><FoundersPage /></CmsPage>} />
        <Route path="/sme" element={<CmsPage page="sme"><SME /></CmsPage>} />
        <Route path="/community" element={<Community />} />
        <Route path="/about" element={<CmsPage page="about"><About /></CmsPage>} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/onboarding/:segment" element={<Onboarding />} />
        <Route path="/startups/:id" element={<StartupDetail />} />
        <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/dealflow" element={<ProtectedRoute roles={["investor", "admin"]}><DealFlow /></ProtectedRoute>} />
        <Route path="/startups/new" element={<ProtectedRoute roles={["founder", "admin"]}><CreateStartup /></ProtectedRoute>} />
        <Route path="/startups/:id/edit" element={<ProtectedRoute roles={["founder", "admin"]}><CreateStartup /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/cms" element={<ProtectedRoute roles={["admin"]}><Cms /></ProtectedRoute>} />
        <Route path="/admin/femtech" element={<ProtectedRoute roles={["admin"]}><FemtechAdmin /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* Business Plan Creator runs as a full-screen standalone app (own top bar + sidebar). */}
      <Route path="/outils/business-plan" element={
        <React.Suspense fallback={null}><BusinessPlanApp /></React.Suspense>
      } />
      <Route path="/outils/business-plan/:id" element={
        <React.Suspense fallback={null}><ProtectedRoute><BusinessPlanWorkspace /></ProtectedRoute></React.Suspense>
      } />
      <Route path="/outils/business-plan/:id/print" element={
        <React.Suspense fallback={null}><ProtectedRoute><BusinessPlanPrint /></ProtectedRoute></React.Suspense>
      } />
      <Route path="/outils/business-plan/:id/:view" element={
        <React.Suspense fallback={null}><ProtectedRoute><BusinessPlanWorkspace /></ProtectedRoute></React.Suspense>
      } />
    </Routes>
  );
}

export default function App() {
  useEffect(() => { captureAttribution(); }, []); // first-touch UTM/referrer
  return (
    <AuthProvider>
      <CurrencyProvider>
      <SiteProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster richColors position="top-right" />
          <Seo />
          <CookieConsent />
          <FeedbackWidget />
          <ErrorBoundary>
            <RootRouter />
          </ErrorBoundary>
        </BrowserRouter>
      </SiteProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
}
